/**
 * Permission matrix (plan p36). Values are default access levels; owners may tighten later.
 *  full    - everything within the tenant (owner: all authorised branches)
 *  branch  - only the branches in the membership's branch scope
 *  self    - only the member's own records (own shift / own performance / own payroll)
 *  limited - allowed up to an approval limit (enforced by the feature module)
 *  request - may create a request that someone else approves
 *  basic   - only the minimum needed fields (e.g. customer name/phone at checkout)
 *  none    - denied
 */
export const ROLES = ["owner", "manager", "cashier", "staff"] as const;
export type Role = (typeof ROLES)[number];
export type Access = "full" | "branch" | "self" | "limited" | "request" | "basic" | "none";

export const PERMISSIONS = {
  "reports.view":         { owner: "full", manager: "branch",  cashier: "self",    staff: "self" },
  "sales.create":         { owner: "full", manager: "full",    cashier: "full",    staff: "none" },
  "refunds.approve":      { owner: "full", manager: "limited", cashier: "request", staff: "none" },
  "discounts.large":      { owner: "full", manager: "limited", cashier: "request", staff: "none" },
  "payroll.rules":        { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  "payroll.own":          { owner: "full", manager: "self",    cashier: "self",    staff: "self" },
  "customers.view":       { owner: "full", manager: "branch",  cashier: "basic",   staff: "self" },
  "data.export":          { owner: "full", manager: "none",    cashier: "none",    staff: "none" },
  "appointments.view":    { owner: "full", manager: "branch",  cashier: "branch",  staff: "self" },
  "appointments.manage":  { owner: "full", manager: "branch",  cashier: "branch",  staff: "none" },
  "customers.manage":     { owner: "full", manager: "branch",  cashier: "basic",   staff: "none" },
  "catalog.manage":       { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  "catalog.view":         { owner: "full", manager: "branch",  cashier: "branch",  staff: "branch" },
  "inventory.manage":     { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  // WP05 finance: P&L / cash flow / liabilities / valuation / AP are owner + manager (own branches) only
  "finance.view":         { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  "purchasing.manage":    { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  "payables.pay":         { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  "expenses.manage":      { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  "team.view":            { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  "branches.manage":      { owner: "full", manager: "none",    cashier: "none",    staff: "none" },
  "branches.view":        { owner: "full", manager: "branch",  cashier: "branch",  staff: "branch" },
  "website.manage":       { owner: "full", manager: "none",    cashier: "none",    staff: "none" },
  "settings.manage":      { owner: "full", manager: "none",    cashier: "none",    staff: "none" },
  "billing.manage":       { owner: "full", manager: "none",    cashier: "none",    staff: "none" },
  "audit.view":           { owner: "full", manager: "none",    cashier: "none",    staff: "none" },
  "cash_session.operate": { owner: "full", manager: "full",    cashier: "full",    staff: "none" },
  // WP06 daily close: cashiers see the day of their branch; locking a day is owner/manager
  "daily_close.view":     { owner: "full", manager: "branch",  cashier: "branch",  staff: "none" },
  "daily_close.lock":     { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  // Task D: WhatsApp/e-mail broadcasts + automations; points / voucher adjustments (cashiers view + redeem only)
  "marketing.manage":     { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  "loyalty.adjust":       { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  // Task K: the AI helper (the owner connects the key in Settings; managers may use it) and bulk customer import
  "ai.use":               { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  "customers.import":     { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  // Task O: attendance (打卡). Everyone clocks themselves in/out; owners/managers see the branch list and correct shifts.
  "attendance.manage":    { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },
  // Task FF: separate permissions so each page group of the owner's permission editor can be switched on its own.
  // Defaults copy the permission that guarded the page before, so nothing changes until the owner edits the matrix.
  "membership.manage":    { owner: "full", manager: "none",    cashier: "none",    staff: "none" },  // was settings.manage
  "subscriptions.view":   { owner: "full", manager: "branch",  cashier: "basic",   staff: "self" },  // was customers.view
  "subscriptions.manage": { owner: "full", manager: "full",    cashier: "full",    staff: "none" },  // was sales.create
  "subscriptions.plans":  { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },  // was catalog.manage
  "requests.approve":     { owner: "full", manager: "branch",  cashier: "none",    staff: "none" },  // was payroll.rules
  "shop.manage":          { owner: "full", manager: "none",    cashier: "none",    staff: "none" },  // was settings.manage
  // Task NN: 投诉与建议 / Feedback to the Senvia team — owner + managers by default; staff / cashiers via the permission editor
  "feedback.view":        { owner: "full", manager: "full",    cashier: "none",    staff: "none" },
  "feedback.submit":      { owner: "full", manager: "full",    cashier: "none",    staff: "none" },
} as const satisfies Record<string, Record<Role, Access>>;

export type Permission = keyof typeof PERMISSIONS;

/**
 * Membership may carry explicit grants that raise a "none" to a level (e.g. staff allowed to sell).
 * (Task FF) "$set" = the owner's permission editor (module levels resolved per permission, see permission-modules.ts):
 * it wins over the role default in both directions. Built at session time, never stored in membership.grants.
 */
export type Grants = Partial<Record<Permission, Access>> & { $set?: Partial<Record<Permission, Access>> };

export function isPermission(p: string): p is Permission {
  return Object.prototype.hasOwnProperty.call(PERMISSIONS, p);
}

export function accessFor(role: Role, permission: Permission, grants: Grants = {}): Access {
  const base = (PERMISSIONS[permission] as Record<Role, Access> | undefined)?.[role] ?? "none";
  if (role !== "owner") { const set = grants.$set?.[permission]; if (set) return set; }
  const granted = grants[permission];
  return granted && base === "none" ? granted : base;
}

export function can(role: Role, permission: Permission, grants: Grants = {}): boolean {
  return accessFor(role, permission, grants) !== "none";
}

export function permissionMap(role: Role, grants: Grants = {}): Record<Permission, Access> {
  const out = {} as Record<Permission, Access>;
  for (const p of Object.keys(PERMISSIONS) as Permission[]) out[p] = accessFor(role, p, grants);
  return out;
}

/** Workspaces (plan p13-17, p28, p33) and which roles may enter them. */
export type Workspace = "owner" | "checkout" | "employee" | "platform";
export const WORKSPACE_ROLES: Record<Exclude<Workspace, "platform">, Role[]> = {
  owner: ["owner", "manager"],
  checkout: ["owner", "manager", "cashier"],
  employee: ["owner", "manager", "cashier", "staff"],
};

export function workspacesFor(role: Role | null, isPlatformAdmin: boolean): Workspace[] {
  const ws: Workspace[] = [];
  if (role) for (const [w, roles] of Object.entries(WORKSPACE_ROLES)) if (roles.includes(role)) ws.push(w as Workspace);
  if (isPlatformAdmin) ws.push("platform");
  return ws;
}
